import * as Hooks from 'preact/hooks';
import { useAsync } from '../hooks/useAsync';
import { useUndoState } from './useUndoState';
import { getPropertyOptions, isChoiceCompatible } from './compatibility';
import { PropertyValue, PropertySelections, PropertyOptions, ResolveSelection } from './types';
import { useCachingSelectionResolver } from './caching';
import { useCachingAsyncCallback } from './useCachingAsyncCallback';
import * as Selectors from '../../store/selectors';
import { useSelector } from '../hooks/redux';

export function useConfiguratorSelectionState(
    spaceId: string | undefined,
    initialSelections: PropertySelections | undefined,
    initialAttachments: Record<string, string> | undefined,
    fixedSelections: PropertySelections | undefined,
    fallbacks?: PropertySelections[],
): {
    resolveSelection?: ResolveSelection;
    propertySelections?: PropertySelections;
    attachmentProposals: Record<string, string>;
    selectAttachment: (attachmentOption: string, value: string) => void;
    selectProperty: (property: string, value: PropertyValue) => void;
    compatiblePropertyOptions?: PropertyOptions;
    undo?: () => void;
    redo?: () => void;
    changes: PropertySelections;
} {
    const gavelUrl = useSelector(Selectors.Core.configurationServiceUrl);
    const tenantId = useSelector(Selectors.Core.tenantIdentifier);

    const {
        current: { propertySelections, attachmentProposals },
        undo,
        redo,
        pushState,
        clear: resetSelections,
    } = useUndoState<{
        propertySelections: PropertySelections | undefined;
        attachmentProposals: Record<string, string>;
    }>({ propertySelections: undefined, attachmentProposals: {} });

    Hooks.useEffect(
        () =>
            initialSelections &&
            initialAttachments &&
            resetSelections({
                propertySelections: { ...initialSelections, ...(fixedSelections ?? {}) },
                attachmentProposals: { ...initialAttachments },
            }),
        [initialSelections, fixedSelections, initialAttachments],
    );

    const getOptions = useCachingAsyncCallback(
        async propertySelections =>
            gavelUrl && spaceId && tenantId
                ? await getPropertyOptions(gavelUrl, tenantId, spaceId, propertySelections)
                : undefined,
        [gavelUrl, tenantId, spaceId],
    );

    const compatiblePropertyOptions = useAsync(
        async () => (propertySelections ? await getOptions(propertySelections) : undefined),
        [getOptions, propertySelections],
    );

    Hooks.useEffect(() => {
        if (compatiblePropertyOptions.result) {
            setResolvingProposal(false);
        }
    }, [compatiblePropertyOptions.result]);

    const [resolvingProposal, setResolvingProposal] = Hooks.useState(false);
    const [changes, setChanges] = Hooks.useState<PropertySelections>({});

    const [pendingSelection, setPendingSelection] = Hooks.useState<[string, PropertyValue] | null>(
        null,
    );

    const selectProperty = (property: string, value: PropertyValue) => {
        if (value === propertySelections?.[property]) return;
        setPendingSelection([property, value]);
    };

    const resolveSelection = useCachingSelectionResolver(
        spaceId,
        propertySelections,
        compatiblePropertyOptions.result,
        fixedSelections,
        fallbacks,
    );

    Hooks.useEffect(() => {
        if (!resolveSelection || !compatiblePropertyOptions.result || !pendingSelection) return;
        setPendingSelection(null);

        const [property, value] = pendingSelection;

        if (!isChoiceCompatible(compatiblePropertyOptions.result, property, value)) {
            setResolvingProposal(true);
        }

        resolveSelection(property, value).then(resolvedPropertySelections => {
            const removedProperties: PropertySelections = {};
            if (propertySelections) {
                Object.keys(propertySelections).forEach(newProperty => {
                    if (
                        newProperty !== property &&
                        propertySelections[newProperty] !== resolvedPropertySelections[newProperty]
                    ) {
                        removedProperties[newProperty] = `${propertySelections[newProperty]}`;
                    }
                });
            }
            setChanges(removedProperties);
            setResolvingProposal(false);
            pushState(({ attachmentProposals: attachmentSelections }) => ({
                propertySelections: resolvedPropertySelections,
                attachmentProposals: attachmentSelections,
            }));
        });
    }, [pendingSelection, resolveSelection]);

    const selectAttachment = (attachmentOption: string, value: string) => {
        pushState({
            propertySelections,
            attachmentProposals: {
                ...attachmentProposals,
                [attachmentOption]: value,
            },
        });
    };

    return {
        resolveSelection,
        propertySelections:
            initialSelections && !resolvingProposal && propertySelections
                ? propertySelections
                : undefined,
        attachmentProposals,
        selectAttachment,
        selectProperty,
        compatiblePropertyOptions: compatiblePropertyOptions.latest,
        undo,
        redo,
        changes,
    };
}
